import { memo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { cn } from "../../../../lib/cn";
import { SourceBadge } from "./SourceBadge";
import { FileAccessRequestCard } from "./FileAccessRequestCard";
import { useFileUrl } from "../../../../lib/react-query/files";

const NUMERIC_CLAIM_LINK = "#dealnest-claim-";

const components = {
  p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-1.5 list-disc pl-4 space-y-0.5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-1.5 list-decimal pl-4 space-y-0.5 last:mb-0">
      {children}
    </ol>
  ),
  code: ({ children }) => (
    <code className="rounded bg-black/5 px-1 py-0.5 text-[0.85em]">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="mb-1.5 overflow-x-auto rounded-md bg-black/5 p-2 text-xs last:mb-0">
      {children}
    </pre>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-primary underline"
    >
      {children}
    </a>
  ),
  h1: ({ children }) => <p className="mb-1 font-semibold">{children}</p>,
  h2: ({ children }) => <p className="mb-1 font-semibold">{children}</p>,
  h3: ({ children }) => <p className="mb-1 font-semibold">{children}</p>,
  h4: ({ children }) => <p className="mb-1 font-semibold">{children}</p>,
};

const stripInlineCitationMarkers = (content) =>
  (content || "")
    .replace(/\s*\[(?:file|knowledge):\d+:\d+\]/gi, "")
    .trim();

const injectNumericClaimTokens = (content, claims) => {
  if (!content || !Array.isArray(claims) || !claims.length) return content;

  let cursor = 0;
  let result = "";
  claims.forEach((claim, index) => {
    if (!claim?.raw) return;
    const start = content.indexOf(claim.raw, cursor);
    if (start < 0) return;
    result += `${content.slice(cursor, start)}[${claim.raw}](${NUMERIC_CLAIM_LINK}${index})`;
    cursor = start + claim.raw.length;
  });
  return `${result}${content.slice(cursor)}`;
};

const citationTitle = (citation) =>
  citation?.file_name ||
  citation?.title ||
  citation?.source_title ||
  citation?.knowledge_title ||
  `${citation?.source_type || "Source"} ${citation?.source_id || ""}`;

const VerifiedClaimReference = ({ claim, citation }) => {
  const index = claim?.citation_index;
  const title = citationTitle(citation);
  const page = citation?.page_range ? `, page ${citation.page_range}` : "";
  const snippet = citation?.snippet || "No source excerpt available.";
  const titleText = `${title}${page}: ${snippet}`;

  return (
    <button
      type="button"
      className="ml-0.5 inline-flex cursor-help align-super text-[10px] font-semibold text-primary/80 underline decoration-dotted underline-offset-2"
      aria-label={`Verified source ${index}`}
      title={titleText}
    >
      [{index}]
    </button>
  );
};

const createLinkRenderer = (numericClaims, citations) => {
  const LinkRenderer = ({ children, href }) => {
    const claimMatch = href?.match(/^#dealnest-claim-(\d+)$/);
    if (claimMatch) {
      const claimIndex = Number(claimMatch[1]);
      const claim = numericClaims[claimIndex];
      return (
        <span key={`numeric-claim-${claimIndex}`}>
          {children}
          <VerifiedClaimReference
            claim={claim}
            citation={citations[Number(claim?.citation_index) - 1]}
          />
        </span>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-primary underline"
      >
        {children}
      </a>
    );
  };

  LinkRenderer.displayName = "ChatMessageLink";
  return LinkRenderer;
};

export const ChatMessageContent = memo(({
  content,
  className,
  fileAccessResolved,
  message,
  sessionId,
  projectId,
}) => {
  const [citationsExpanded, setCitationsExpanded] = useState(false);
  const getFileUrl = useFileUrl();
  const citationCount = Array.isArray(message?.citations) ? message.citations.length : 0;
  const numericClaims = Array.isArray(message?.metadata?.numeric_claims)
    ? message.metadata.numeric_claims
    : [];
  const messageContent = injectNumericClaimTokens(
    stripInlineCitationMarkers(content),
    numericClaims,
  );
  const outputFiles = Array.isArray(message?.metadata?.output_files)
    ? message.metadata.output_files
    : [];

  const downloadOutput = async (file) => {
    const tab = window.open("about:blank", "_blank");
    try {
      const url = await getFileUrl.mutateAsync({ projectId, fileId: file.id });
      if (tab) {
        tab.opener = null;
        tab.location = url;
      }
      else window.location.assign(url);
    } catch (_error) {
      tab?.close();
    }
  };

  return (
    <div className={cn("text-sm leading-relaxed", className)}>
      {message?.metadata?.intent === "file_access_request" ? (
        <FileAccessRequestCard
          message={message}
          sessionId={sessionId}
          resolved={fileAccessResolved}
        />
      ) : null}
      {message?.evidence_state === "UNSUPPORTED" ? (
        <div className="mb-2 rounded-lg border border-warning/20 bg-warning/10 px-2 py-1.5 text-xs text-warning">
          No sourced evidence in the current selection.
        </div>
      ) : null}
      {message?.metadata?.intent !== "file_access_request" && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={{
            ...components,
            a: createLinkRenderer(numericClaims, message?.citations || []),
          }}
        >
          {messageContent}
        </ReactMarkdown>
      )}
      {outputFiles.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2" aria-label="Created files">
          {outputFiles.map((file) => (
            <button
              key={file.id}
              type="button"
              disabled={!projectId || getFileUrl.isPending}
              onClick={() => downloadOutput(file)}
              className="rounded-md border border-primary/20 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/5 disabled:opacity-50"
            >
              Download {file.name}
            </button>
          ))}
        </div>
      ) : null}
      {citationCount > 0 ? (
        citationsExpanded ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {message.citations.map((_citation, index) => (
              <SourceBadge
                key={`${message.id}-citation-${index}`}
                index={index + 1}
                sessionId={sessionId}
                messageId={message.id}
              />
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCitationsExpanded(true)}
            className="mt-1.5 text-[11px] text-primary/70 hover:text-primary transition-colors"
          >
            {citationCount} {citationCount === 1 ? "source" : "sources"}
          </button>
        )
      ) : null}
    </div>
  );
});

ChatMessageContent.displayName = "ChatMessageContent";
